import { compact } from 'lodash-es'
import {
  type HTMLAttributes,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'
import {
  type ContextValue as AriaContextValue,
  Header as AriaHeader,
  Menu as AriaMenu,
  MenuContext as AriaMenuContext,
  MenuItem as AriaMenuItem,
  type MenuProps as AriaMenuProps,
  MenuTrigger as AriaMenuTrigger,
  type MenuTriggerProps as AriaMenuTriggerProps,
  Popover as AriaPopover,
  PopoverContext as AriaPopoverContext,
  type PopoverProps as AriaPopoverProps,
  Separator as AriaSeparator,
  type SeparatorProps as AriaSeparatorProps,
  SubmenuTrigger as AriaSubmenuTrigger,
  Text as AriaText,
  type Key,
} from 'react-aria-components'
import { cx } from 'src/utils/cx'
import { type VariantProps, tv } from 'tailwind-variants'
import { P, isMatching, match } from 'ts-pattern'
import FluentChevronRight16Filled from '~icons/fluent/chevron-right-16-filled'
import MingcuteCheckFill from '~icons/mingcute/check-fill'

export type UIMenuTriggerProps =
  | {
      // When using render prop to generate menu content, the `id` prop is necessary
      // when you don't have an `id` or `key` field in the item data.
      id?: Key
      children: ReactElement[]
    }
  | {
      children: ReactNode
      onOpenChange?: AriaMenuTriggerProps['onOpenChange']
    }

export function UIMenuTrigger(props: UIMenuTriggerProps) {
  const ariaMenuContext = useContext(AriaMenuContext)

  return (
    match(props)
      .with(
        { children: P.array(P.when(isValidElement)) },
        // Having a menu context means this is a submenu trigger.
        // The `props` arg is necessary for ts-pattern to pass type info.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        props => Boolean(ariaMenuContext),
        props => <AriaSubmenuTrigger>{props.children}</AriaSubmenuTrigger>,
      )
      // Not sure how to make TS auto-infer the type of `onOpenChange` here
      .with({ onOpenChange: P.optional(P.any) }, props => (
        <AriaMenuTrigger onOpenChange={props.onOpenChange}>
          {props.children}
        </AriaMenuTrigger>
      ))
      .otherwise(() => null)
  )
}

export function UIMenuSeparator(props: Pick<AriaSeparatorProps, 'id'>) {
  return (
    <AriaSeparator
      id={props.id}
      className="col-span-full -mx-1 my-1 border-t border-black/10 dark:border-white/10"
    />
  )
}

export function UIMenuLabel(props: HTMLAttributes<HTMLElement>) {
  return (
    <AriaHeader
      {...props}
      className={cx(
        'col-span-full flex items-center h-7 px-2 text-xs',
        props.className,
      )}
    >
      {props.children}
    </AriaHeader>
  )
}

/*
 * Menu context
 */

const UIMenuContext = createContext({
  isRoot: true,
})

function UIMenuProvider(props: PropsWithChildren) {
  const menuContext = useContext(UIMenuContext)

  return (
    <UIMenuContext.Provider
      value={{
        isRoot: !menuContext,
      }}
    >
      {props.children}
    </UIMenuContext.Provider>
  )
}

/*
 * Menu container
 */

const menuStyles = tv({
  slots: {
    popover: [
      // Entering animation
      'entering:animate-in',
      'entering:fade-in',
      'entering:data-[placement=top]:slide-in-from-bottom-1',
      'entering:data-[placement=bottom]:slide-in-from-top-1',
      'entering:data-[placement=left]:slide-in-from-right-1',
      'entering:data-[placement=right]:slide-in-from-left-1',
      // Exiting animation
      'exiting:animate-out',
      'exiting:fade-out',
      'exiting:data-[placement=top]:slide-out-to-bottom-1',
      'exiting:data-[placement=bottom]:slide-out-to-top-1',
      'exiting:data-[placement=left]:slide-out-to-right-1',
      'exiting:data-[placement=right]:slide-out-to-left-1',
    ],
    menu: [
      'grid grid-cols-[auto_1fr_auto] min-w-[100px] max-h-[inherit] p-1 overflow-auto text-sm rounded-lg ring-1 shadow-lg outline-none',
      // Light
      'bg-white ring-black/20',
      // Dark
      'dark:bg-zinc-700 dark:ring-white/20 dark:shadow-black/20',
    ],
  },
})

export const UIMenuSeparatorSymbol = Symbol('menu separator')

export interface UIMenuProps<T> extends StyleProps {
  /**
   * The data representing the menu items.
   * Should contain an `id` or `key` prop, or pass an `id` prop to <UIMenuItem>
   * in the render prop.
   * `MENU_SEPARATOR` renders a separator and will not be passed to the render
   * prop.
   */
  items?: AriaMenuProps<T | typeof UIMenuSeparatorSymbol>['items']
  children?: AriaMenuProps<T>['children']
  onAction?: AriaMenuProps<T>['onAction']

  selectionMode?: AriaMenuProps<T>['selectionMode']
  selectedKeys?: AriaMenuProps<T>['selectedKeys']
  onSelectionChange?: AriaMenuProps<T>['onSelectionChange']

  placement?: AriaPopoverProps['placement']
}

type SeparatorItem = { id: string; type: 'separator' }

export function UIMenu<T extends object>(props: UIMenuProps<T>) {
  const ctx = useContext(UIMenuContext)

  const ariaPopoverCtx = useContext(AriaPopoverContext)
  const triggerRef = useRef<HTMLElement | null>(null)

  triggerRef.current = resolveTrigger(ariaPopoverCtx)

  const normalizedItems = useMemo(
    () =>
      props.items &&
      Array.from(props.items)?.map((item, idx) =>
        item === UIMenuSeparatorSymbol
          ? ({ id: `sep-${idx}`, type: 'separator' } as const)
          : item,
      ),
    [props.items],
  )
  const renderItems = useCallback(
    (item: T | SeparatorItem) => {
      if (typeof props.children === 'function') {
        return isMatching({ id: P.string, type: 'separator' }, item) ? (
          <UIMenuSeparator id={item.id} />
        ) : (
          props.children(item)
        )
      } else {
        return props.children
      }
    },
    [props],
  )

  const styles = menuStyles()

  return (
    <UIMenuProvider>
      <AriaPopover
        triggerRef={triggerRef}
        placement={props.placement}
        offset={ctx.isRoot ? 4 : 0}
        crossOffset={ctx.isRoot ? 0 : -4}
        className={styles.popover()}
      >
        <AriaMenu
          items={normalizedItems}
          onAction={props.onAction}
          selectionMode={props.selectionMode}
          selectedKeys={props.selectedKeys}
          onSelectionChange={props.onSelectionChange}
          className={styles.menu({ className: props.className })}
          style={props.style}
        >
          {typeof props.children === 'function' ? renderItems : props.children}
        </AriaMenu>
      </AriaPopover>
    </UIMenuProvider>
  )
}

UIMenu.compact = <T extends object>(
  items: Array<T | typeof UIMenuSeparatorSymbol | Falsy>,
) => {
  const compacted = compact(items)
  return compacted.slice(
    compacted.findIndex(it => it !== UIMenuSeparatorSymbol),
    compacted.findLastIndex(it => it !== UIMenuSeparatorSymbol) + 1,
  )
}

function resolveTrigger(ctx: AriaContextValue<AriaPopoverProps, HTMLElement>) {
  const trigger =
    ctx && 'triggerRef' in ctx ? ctx.triggerRef?.current : undefined
  return (trigger?.querySelector('[data-ref-caret]') as HTMLElement) ?? trigger
}

/*
 * Menu item
 */

const menuItemStyles = tv({
  slots: {
    root: 'col-span-full grid grid-cols-subgrid items-center min-h-8 rounded outline-none cursor-default',
    icon: 'col-start-1 inline-grid place-content-center min-w-2.5',
    label: 'col-start-2 py-1',
    caret:
      'col-start-3 self-stretch inline-grid justify-end items-center min-w-2.5 text-neutral-600 dark:text-neutral-400',
    description: 'col-start-2 mb-1 text-xs text-neutral-500',
  },
  variants: {
    color: {
      normal: {
        root: [
          'hover:bg-black/5 focus-visible:bg-black/5 open:bg-black/5',
          'dark:hover:bg-white/5 dark:focus-visible:bg-white/5 dark:open:bg-white/5',
        ],
      },
      danger: {
        root: [
          'text-red-600 dark:text-red-500 hover:!text-white focus-visible:!text-white open:!text-white',
          'hover:bg-red-600 focus-visible:bg-red-600 open:bg-red-600',
          'dark:hover:bg-red-500 dark:focus-visible:bg-red-500 dark:open:bg-red-500',
        ],
      },
      warning: {
        root: [
          'text-orange-600 dark:text-orange-500 hover:!text-white focus-visible:!text-white open:!text-white',
          'hover:bg-orange-600 focus-visible:bg-orange-600 open:bg-orange-600',
          'dark:hover:bg-orange-500 dark:focus-visible:bg-orange-500 dark:open:bg-orange-500',
        ],
      },
      admin: {
        root: [
          'text-amber-600 hover:bg-amber-100 focus-visible:bg-amber-100 open:bg-amber-100',
          'dark:text-amber-500 dark:hover:bg-amber-500/10 dark:focus-visible:bg-amber-500/10 dark:open:bg-amber-500/10',
        ],
      },
    },
    disabled: {
      true: {
        root: 'opacity-50',
      },
    },
  },
  defaultVariants: {
    color: 'normal',
    disabled: false,
  },
})

export interface UIMenuItemProps
  extends StyleProps,
    VariantProps<typeof menuItemStyles> {
  id?: Key
  icon?: ReactNode
  textValue?: string
  description?: ReactNode
  onAction?: () => void
  children?: ReactNode
}

export function UIMenuItem(props: UIMenuItemProps) {
  const styles = menuItemStyles({
    color: props.color,
    disabled: props.disabled,
  })

  return (
    <AriaMenuItem
      id={props.id}
      textValue={props.textValue}
      isDisabled={props.disabled}
      className={styles.root({ className: props.className })}
      style={props.style}
      onAction={props.onAction}
    >
      {({ selectionMode, isSelected, hasSubmenu }) => (
        <>
          <span
            className={styles.icon({
              className: (props.icon || selectionMode !== 'none') && 'size-8',
            })}
          >
            {isSelected ? (
              selectionMode === 'multiple' ? (
                <MingcuteCheckFill className="size-3" />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 10 10"
                  className="size-1.5"
                >
                  <circle cx="5" cy="5" r="5" fill="currentColor" />
                </svg>
              )
            ) : (
              props.icon
            )}
          </span>
          <div className={styles.label()}>{props.children}</div>
          <span
            data-ref-caret=""
            className={styles.caret({
              className: hasSubmenu && 'w-4 ml-4',
            })}
          >
            {hasSubmenu && <FluentChevronRight16Filled className="size-4" />}
          </span>
          {props.description && (
            <p className={styles.description()}>
              <AriaText slot="description">{props.description}</AriaText>
            </p>
          )}
        </>
      )}
    </AriaMenuItem>
  )
}
